import { createAvatar } from '@dicebear/core';
import * as notionists from '@dicebear/notionists';
import * as shapes from '@dicebear/shapes';
import { useMemo } from 'react';
import { cn } from '../lib/cn';

// Generated vector avatars: the same seed always draws the same picture, made in the browser with no request to
// another site. Both styles are CC0 (public domain). People get a drawn face, companies an abstract mark.

/** Light brand tints behind the drawing (hex without #, as DiceBear wants them) */
const BACKGROUNDS = ['fde8cc', 'fbd9ae', 'dbe5f1', 'c9d7ea', 'e7ecf3'];
/** Shape colours for companies: brand orange, navy and their tints */
const SHAPE_COLORS = ['f28c1c', '0b213f', '3a5a85', 'f6b066', '8aa3c4'];

const sizes = {
  xs: 'size-6',
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-14',
  xl: 'size-20',
} as const;

interface AvatarProps {
  /** A stable id (user or company id) so the picture never changes; falls back to the name */
  seed?: string;
  /** Used for the accessible name when `decorative` is false, and as the seed fallback */
  name: string;
  kind?: 'person' | 'company';
  size?: keyof typeof sizes;
  /** Most avatars sit next to the name already, so screen readers skip them by default */
  decorative?: boolean;
  className?: string;
}

export function Avatar({
  seed,
  name,
  kind = 'person',
  size = 'md',
  decorative = true,
  className,
}: AvatarProps) {
  const key = seed || name;
  const src = useMemo(
    () =>
      (kind === 'company'
        ? createAvatar(shapes, {
            seed: key,
            backgroundColor: BACKGROUNDS,
            shape1Color: SHAPE_COLORS,
            shape2Color: SHAPE_COLORS,
            shape3Color: SHAPE_COLORS,
          })
        : createAvatar(notionists, { seed: key, backgroundColor: BACKGROUNDS })
      ).toDataUri(),
    [key, kind],
  );
  return (
    <img
      src={src}
      alt={decorative ? '' : name}
      aria-hidden={decorative || undefined}
      draggable={false}
      className={cn(
        'shrink-0 bg-muted object-cover',
        kind === 'company' ? 'rounded-lg' : 'rounded-full',
        sizes[size],
        className,
      )}
    />
  );
}
